import { gql } from '@apollo/client'
import {
  CopyContentButton,
  DocSearchElement,
  DocToolbar,
  FEATURE_CODES,
  PartialGA4FeatureUseEventProps,
  useMetrics,
} from '@jusbrasil-web/docview/core'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { useBoxModal } from '@jusbrasil-web/shared-box-lock'
import { COMPONENT_CLASS, FEATURE_NAME } from '@jusbrasil-web/shared-ga4-events'
import { RefObject } from 'react'

import { ConvertModelButton } from '../convert-model-button'
import {
  ModelToolbar_DocumentFragment,
  ModelToolbar_ViewerFragment,
} from './__generated__/model-toolbar.graphql'
import styles from './model-toolbar.module.scss'

export interface ModelToolbarProps {
  document: ModelToolbar_DocumentFragment
  viewer: ModelToolbar_ViewerFragment | null
  docSearchRef: RefObject<DocSearchElement>
  docContentRef: RefObject<HTMLDivElement>
  ga4DocumentSaveEventData?: PartialGA4FeatureUseEventProps
  onCopyContent?: (successfulAttempt: boolean) => void
  onDocumentDownload?: (successfulAttempt: boolean) => void
  componentClass?: COMPONENT_CLASS
  disableRenderMetrics?: boolean
}

const boxProps = {
  featureCode: FEATURE_CODES.COPY_MODELOS_CONTENT,
  fromComponent: 'docview/ModelToolbar',
}

export function ModelToolbar(props: ModelToolbarProps) {
  const {
    document,
    viewer,
    docSearchRef,
    docContentRef,
    componentClass,
    ga4DocumentSaveEventData,
    onCopyContent,
    onDocumentDownload,
  } = props
  const { ref: dispatcherRef, sendMetrics, sendCopyEvents } = useMetrics<HTMLDivElement>()
  const { setOpen } = useBoxModal({
    appId: 'web-docview',
    boxOptions: {},
    pageMetadata: {},
    viewer,
    enabled: !document.canCopyContent,
    ...boxProps,
  })

  const onCopyContentButtonClick = () => {
    sendCopyEvents()
    onCopyContent?.(true)
  }

  const shouldAllowCopy = () => {
    if (!viewer) {
      onCopyContent?.(false)
      return false
    }

    const hasPendingPayment = !!viewer.subscriptions?.some((s) => s?.overdue)
    if (hasPendingPayment || !document.canCopyContent) {
      setOpen(true)
      sendMetrics('ModeloPeca.SawCopyContentLock')
      onCopyContent?.(false)
      return false
    }

    return true
  }

  return (
    <DocToolbar
      document={document}
      viewer={viewer}
      docSearchRef={docSearchRef}
      ga4SearchEventData={{
        componentClass: componentClass || COMPONENT_CLASS.DOCUMENT_HEADER,
        featureName: FEATURE_NAME.MODEL_DOCUMENT_SEARCH,
      }}
      ga4DocumentSaveEventData={ga4DocumentSaveEventData}
      mainActions={
        <>
          <ConvertModelButton document={document} viewer={viewer} onClick={onDocumentDownload} />

          <LoginTrigger
            isLogged={!!viewer}
            options={{
              eventData: {
                feature_code: boxProps.featureCode,
                from_component: boxProps.fromComponent,
              },
            }}
          >
            <CopyContentButton
              contentRef={docContentRef}
              onClick={onCopyContentButtonClick}
              shouldAllowCopy={shouldAllowCopy}
              className={styles.copyBtn}
            >
              Copiar<span data-btn-fragment> modelo</span>
            </CopyContentButton>
          </LoginTrigger>
        </>
      }
      ref={dispatcherRef}
    />
  )
}

ModelToolbar.fragments = {
  document: gql`
    fragment ModelToolbar_document on Document {
      canCopyContent: viewerCanUseFeature(featureCode: "copy_modelos_content")

      ...DocToolbar_document
      ...ConvertModelButton_document
    }

    ${DocToolbar.fragments.document}
    ${ConvertModelButton.fragments.document}
  `,

  viewer: gql`
    fragment ModelToolbar_viewer on Profile {
      subscriptions {
        overdue
      }

      ...DocToolbar_viewer
      ...ConvertModelButton_viewer
      ...useBoxModal_viewer
    }

    ${DocToolbar.fragments.viewer}
    ${ConvertModelButton.fragments.viewer}
    ${useBoxModal.fragments.viewer}
  `,
}
